import { lazy } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';

import { withSuspense } from '@/components/ui/withSuspense';
import { Layout } from '@/components/layout/Layout';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { ProtectedRoute } from './ProtectedRoute';
import { RootLayout } from '@/components/layout/RootLayout';
import { ComingSoonWrapper } from '@/components/features/ComingSoonWrapper';
import { routePaths } from './routePaths';


// Public Pages
const PublicHome = withSuspense(lazy(() => import('@/pages/public/PublicHome')));
const PrivacyPolicy = withSuspense(lazy(() => import('@/pages/public/PrivacyPolicy')));
const TermsOfService = withSuspense(lazy(() => import('@/pages/public/TermsOfService')));
const Help = withSuspense(lazy(() => import('@/pages/public/Help')));
const Pricing = withSuspense(lazy(() => import('@/pages/public/Pricing')));

// Auth Pages
const SignOutPage = withSuspense(lazy(() => import('@/pages/auth/SignOutPage')));
const SignUpPage = withSuspense(lazy(() => import('@/pages/auth/SignUpPage')));
const SignInPage = withSuspense(lazy(() => import('@/pages/auth/SignInPage')));
const VerifyOtpPage = withSuspense(lazy(() => import('@/pages/auth/VerifyOtpPage')));
const ForgotPasswordPage = withSuspense(lazy(() => import('@/pages/auth/ForgotPasswordPage')));
const ResetPasswordPage = withSuspense(lazy(() => import('@/pages/auth/ResetPasswordPage')));
const OAuthCallback = withSuspense(lazy(() => import('@/pages/auth/OAuthCallback')));

// Account Pages
const AccountProfilePage = withSuspense(lazy(() => import('@/pages/account/AccountProfilePage')));
const AccountSettingsPage = withSuspense(lazy(() => import('@/pages/account/AccountSettingsPage')));
const AccountBillingPage = withSuspense(lazy(() => import('@/pages/account/AccountBillingPage')));

// Team Pages
const TeamOverviewPage = withSuspense(lazy(() => import('@/pages/team/TeamOverviewPage')));
const TeamSettingsPage = withSuspense(lazy(() => import('@/pages/team/TeamSettingsPage')));
const TeamMembersPage = withSuspense(lazy(() => import('@/pages/team/TeamMembersPage')));
const TeamBillingPage = withSuspense(lazy(() => import('@/pages/team/TeamBillingPage')));

// App Pages
const Home = withSuspense(lazy(() => import('@/pages/Home')));
const YouTube = withSuspense(lazy(() => import('@/pages/youtube/YouTube')));
const YouTubeAnalytics = withSuspense(lazy(() => import('@/pages/youtube/YouTubeAnalytics')));
const YouTubeComments = withSuspense(lazy(() => import('@/pages/youtube/YouTubeComments')));

// Error Page (optional: keep eager if needed for fallback)
import { ErrorPage } from '@/pages/ErrorPage';

const routes: RouteObject[] = [
  // Auth routes
  {
    path: routePaths.AUTH.ROOT,
    element: <ComingSoonWrapper><AuthLayout /></ComingSoonWrapper>, // Add this if you want a separate layout for auth
    errorElement: <ErrorPage />,
    children: [
      { index: true, element: <Navigate to={routePaths.AUTH.SIGN_IN} replace /> },
      {
        path: routePaths.AUTH.SIGN_IN,
        element: <SignInPage />,
      },
      {
        path: routePaths.AUTH.SIGN_UP,
        element: <SignUpPage />,
      },
      {
        path: routePaths.AUTH.VERIFY_OTP,
        element: <VerifyOtpPage />,
      },
      {
        path: routePaths.AUTH.OAUTH_CALLBACK,
        element: <OAuthCallback />,
      },
      { path: routePaths.AUTH.FORGOT_PASSWORD, element: <ForgotPasswordPage /> },
      { path: routePaths.AUTH.RESET_PASSWORD, element: <ResetPasswordPage /> },
      { path: routePaths.AUTH.SIGN_OUT, element: <SignOutPage /> },
    ],
  },

  {
    path: routePaths.APP.ROOT,
    element: <RootLayout />,
    errorElement: <ErrorPage />,
    children: [

      // 🟢 Public/legal/help pages
      {
        element: <PublicLayout />,
        children: [
          {
            index: true,
            element: <PublicHome />
          },
          {
            path: routePaths.PUBLIC.PRIVACY,
            element: <PrivacyPolicy />
          },
          {
            path: routePaths.PUBLIC.TERMS,
            element: <TermsOfService />,
          },
          {
            path: routePaths.PUBLIC.HELP,
            element: <Help />,
          },
          {
            path: routePaths.PUBLIC.PRICING,
            element: <Pricing />
          }
        ]
      },

      // Authenticated app
      {
        path: routePaths.APP.ROOT,
        element: <ComingSoonWrapper><ProtectedRoute /></ComingSoonWrapper>, // Redirects if not authenticated
        children: [
          {
            element: <Layout />, // your Dashboard layout with PageWrapper + SiteLayout
            children: [
              {
                path: routePaths.APP.DASHBOARD,
                element: <Home />  // DashBoard
              },
              // {
              //   path: routePaths.APP.ONBOARDING,
              //   element: <ONBOARDING /> // TODO
              // },
              {
                path: routePaths.APP.ACCOUNT.ROOT,
                children: [
                  {
                    index: true,
                    element: <AccountProfilePage />   // default /account
                  },
                  {
                    path: routePaths.APP.ACCOUNT.SETTINGS,
                    element: <AccountSettingsPage />  // /account/settings
                  },
                  {
                    path: routePaths.APP.ACCOUNT.BILLINGS,
                    element: <AccountBillingPage />   // /account/billings
                  }
                ]
              },
              {
                path: routePaths.APP.TEAM.ROOT,
                children: [
                  {
                    index: true,
                    element: <TeamOverviewPage />     // default /team
                  },
                  {
                    path: routePaths.APP.TEAM.SETTINGS,
                    element: <TeamSettingsPage />     // /team/settings
                  },
                  {
                    path: routePaths.APP.TEAM.MEMBERS,
                    element: <TeamMembersPage />      // /team/members
                  },
                  {
                    path: routePaths.APP.TEAM.BILLING,
                    element: <TeamBillingPage />      // /team/billing
                  }
                ]
              },
              {
                path: routePaths.APP.YOUTUBE.ROOT,
                children: [
                  { index: true, element: <YouTube /> },
                  { path: routePaths.APP.YOUTUBE.ANALYTICS, element: <YouTubeAnalytics /> },
                  { path: routePaths.APP.YOUTUBE.COMMENTS, element: <YouTubeComments /> },
                  { path: routePaths.APP.YOUTUBE.LIVECHAT, element: <YouTubeComments /> },
                ],
              },
              {
                path: routePaths.APP.TIKTOK.ROOT,
                children: [
                  { index: true, element: <YouTube /> },
                  { path: routePaths.APP.TIKTOK.ANALYTICS, element: <YouTubeAnalytics /> },
                  { path: routePaths.APP.TIKTOK.COMMENTS, element: <YouTubeComments /> },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(routes);